/**
 * Authentication Context with Integrated Sync System
 * 
 * Manages user authentication and initializes the bandwidth-constrained
 * merkle tree sync system on login/logout.
 */

/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { treeCache } from '../lib/sync/merkle-tree';
import { 
  loginRateLimiter, 
  formatRateLimitError, 
  shouldTriggerCaptcha,
  recordFailedLoginForCaptcha,
  resetCaptchaCounter
} from '../lib/utils/rateLimiter';
import { createLogger } from '../lib/utils/logger';
import { useBackgroundSync } from '@/hooks/useBackgroundSync';
import { SessionExpiryToast } from '@/components/common/SessionExpiryToast';

const logger = createLogger('AuthContext');

export interface User {
  id: string;
  email: string;
  user_metadata?: {
    organization_id: string;
    vessel_id: string;
    full_name?: string;
    role?: string;
  };
  // Computed/flat properties for convenience
  organizationId?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  username?: string;
  status?: string;
  lastLoginDate?: Date;
  passwordChangedDate?: Date;
}

interface AuthContextType {
  user: User | null;
  currentUser: User | null; // Alias for user (for backward compatibility)
  loading: boolean;
  isLoading: boolean; // Alias for loading
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  
  // Convenience getters
  userId: string | null;
  organizationId: string | null;
  
  // Role-based access
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  isCompanyAdmin: boolean;
  isShipAdmin: boolean;
  
  // Sync-related
  isSyncing: boolean;
  lastSyncTime: Date | null;
  syncError: string | null;
  bandwidthUsage: ReturnType<typeof useBackgroundSync>['bandwidthUsage'];
  bandwidthSettings: ReturnType<typeof useBackgroundSync>['bandwidthSettings'];
  updateBandwidthSettings: (settings: Parameters<ReturnType<typeof useBackgroundSync>['updateBandwidthSettings']>[0]) => void;
  triggerManualSync: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Background sync (extracted to hook)
  const {
    isSyncing,
    lastSyncTime,
    syncError,
    bandwidthUsage,
    bandwidthSettings,
    initializeSync,
    stopBackgroundSync,
    updateBandwidthSettings,
    triggerManualSync,
    resetSyncState,
  } = useBackgroundSync(
    !!user,
    user?.user_metadata?.organization_id ?? user?.organizationId ?? null
  );

  /**
   * Tables to sync
   * These are the main data tables that need bidirectional sync
   * Memoized to prevent recreation on every render
   */
  // Syncable tables moved into useBackgroundSync

  /**
   * Background sync scheduler with change detection
   */
  // Background sync scheduler moved to hook

  /**
   * Initialize sync system on login
   */
  // initializeSync provided by hook

  /**
   * Stop background sync scheduler
   */
  // stopBackgroundSync provided by hook

  /**
   * Manual sync trigger
   */
  // triggerManualSync provided by hook

  /**
   * Update bandwidth settings
   */
  // updateBandwidthSettings provided by hook

  /**
   * Handle login
   */
  const login = useCallback(async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);

      // Check rate limit BEFORE attempting login
      const rateLimitResult = loginRateLimiter.check(email);
      
      if (!rateLimitResult.allowed) {
        const errorMsg = formatRateLimitError(rateLimitResult, loginRateLimiter);
        setError(errorMsg);
        throw new Error(errorMsg);
      }

      // Show remaining attempts warning if low
      if (rateLimitResult.remaining <= 2 && rateLimitResult.remaining > 0) {
        logger.warn('Login attempts remaining', { remaining: rateLimitResult.remaining });
      }

      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        // Record failed login attempt
        loginRateLimiter.record(email);
        recordFailedLoginForCaptcha(email);
        
        // Check if CAPTCHA should be triggered
        const shouldShowCaptcha = shouldTriggerCaptcha(email);
        if (shouldShowCaptcha) {
          logger.warn('CAPTCHA should be triggered after multiple failed attempts');
          // TODO: Integrate CAPTCHA service (e.g., reCAPTCHA, hCaptcha)
        }
        
        throw authError;
      }

      // Successful login - reset rate limiters
      loginRateLimiter.reset(email);
      resetCaptchaCounter(email);

      // Fetch user record from users table to get organization_id and other details
      const { data: userRecord, error: userError } = await supabase
        .from('users')
        .select('id, organization_id, email, first_name, last_name, role, is_active')
        .eq('id', data.user?.id)
        .single();

      if (userError) {
        logger.error('Failed to fetch user record', userError);
        // Continue with auth user data, but log warning
        logger.warn('Proceeding without user record - organization_id may be missing');
      }

      // Use user record data if available, otherwise fall back to auth metadata
      const organizationId = userRecord?.organization_id || (data.user?.user_metadata as any)?.organization_id;
      const role = userRecord?.role || (data.user?.user_metadata as any)?.role;
      const firstName = userRecord?.first_name || (data.user?.user_metadata as any)?.first_name;
      const lastName = userRecord?.last_name || (data.user?.user_metadata as any)?.last_name;
      const fullName = userRecord?.first_name && userRecord?.last_name 
        ? `${userRecord.first_name} ${userRecord.last_name}`
        : (data.user?.user_metadata as any)?.full_name || '';

      const metadata = {
        organization_id: organizationId,
        vessel_id: (data.user?.user_metadata as any)?.vessel_id,
        full_name: fullName,
        role: role,
      } as User['user_metadata'];
      
      const userData: User = {
        id: data.user?.id || '',
        email: data.user?.email || '',
        user_metadata: metadata,
        organizationId: organizationId,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        role: role,
      };

      setUser(userData);

      // Initialize sync system
      await initializeSync();

      logger.info('Login successful');
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Login failed';
      setError(errorMsg);
      setUser(null);
      logger.error('Login error', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [initializeSync]);

  /**
   * Handle logout
   */
  const logout = useCallback(async () => {
    try {
      setError(null);

      // Stop background sync
      stopBackgroundSync();

      // Clear tree cache
      treeCache.clear();

    // Clear UI state
    resetSyncState();

      // Sign out from Supabase
      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) throw signOutError;

      setUser(null);
      logger.info('Logout successful');
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Logout failed';
      setError(errorMsg);
      logger.error('Logout error', error);
      throw error;
    }
  }, [stopBackgroundSync, resetSyncState]);

  /**
   * Sprint 2025-11-012: Session expiry monitoring and activity tracking
   */
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);
  
  useEffect(() => {
    if (!user) {
      setShowExpiryWarning(false);
      return;
    }

    // Check session expiry every 30 seconds
    const checkExpiry = async () => {
      const { encryptedStorage } = await import('@/lib/auth/encryptedStorage');
      
      // Check if session expired
      if (encryptedStorage.isSessionExpiredNow()) {
        logger.warn('Session expired - logging out');
        await logout();
        // Navigate to login with session_expired flag
        window.location.href = '/login?session_expired=true';
        return;
      }
      
      // Check if should show warning (< 5 minutes remaining)
      const shouldWarn = encryptedStorage.shouldShowExpiryWarning();
      setShowExpiryWarning(shouldWarn);
    };

    checkExpiry(); // Initial check
    const expiryInterval = setInterval(checkExpiry, 30000); // Check every 30 seconds

    // Activity tracking - extend session on user activity
    let lastActivityTime = Date.now();
    const ACTIVITY_THROTTLE = 30000; // 30 seconds

    const handleActivity = async () => {
      const now = Date.now();
      if (now - lastActivityTime < ACTIVITY_THROTTLE) {
        return; // Throttle activity updates
      }
      
      lastActivityTime = now;
      const { encryptedStorage } = await import('@/lib/auth/encryptedStorage');
      const extended = encryptedStorage.extendSession();
      
      if (extended) {
        logger.debug('Session extended by activity');
        setShowExpiryWarning(false); // Dismiss warning if shown
      }
    };

    // Register activity listeners
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keypress', handleActivity);
    window.addEventListener('click', handleActivity);

    return () => {
      clearInterval(expiryInterval);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keypress', handleActivity);
      window.removeEventListener('click', handleActivity);
    };
  }, [user, logout]);

  /**
   * Handle session extension (from toast button)
   */
  const handleExtendSession = useCallback(async () => {
    const { encryptedStorage } = await import('@/lib/auth/encryptedStorage');
    const extended = encryptedStorage.extendSession();
    
    if (extended) {
      logger.info('Session manually extended by user');
      setShowExpiryWarning(false);
    }
  }, []);

  /**
   * Check for existing session on mount
   */
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();

        if (error) throw error;

        if (data.session?.user) {
          // Fetch user record from users table to get organization_id
          const { data: userRecord } = await supabase
            .from('users')
            .select('id, organization_id, email, first_name, last_name, role, is_active')
            .eq('id', data.session.user.id)
            .single();

          // Use user record data if available, otherwise fall back to auth metadata
          const organizationId = userRecord?.organization_id || (data.session.user.user_metadata as any)?.organization_id;
          const role = userRecord?.role || (data.session.user.user_metadata as any)?.role;
          const firstName = userRecord?.first_name || (data.session.user.user_metadata as any)?.first_name;
          const lastName = userRecord?.last_name || (data.session.user.user_metadata as any)?.last_name;
          const fullName = userRecord?.first_name && userRecord?.last_name 
            ? `${userRecord.first_name} ${userRecord.last_name}`
            : (data.session.user.user_metadata as any)?.full_name || '';

          const metadata = {
            organization_id: organizationId,
            vessel_id: (data.session.user.user_metadata as any)?.vessel_id,
            full_name: fullName,
            role: role,
          } as User['user_metadata'];
          
          const userData: User = {
            id: data.session.user.id,
            email: data.session.user.email || '',
            user_metadata: metadata,
            organizationId: organizationId,
            firstName: firstName || undefined,
            lastName: lastName || undefined,
            role: role,
          };

          setUser(userData);

          // Initialize sync for existing session
          await initializeSync();
        }
      } catch (err) {
        logger.error('Session check failed:', err);
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          const metadata = session.user.user_metadata as User['user_metadata'];
          const fullName = metadata?.full_name || '';
          const [firstName, ...lastNameParts] = fullName.split(' ');
          const lastName = lastNameParts.join(' ');
          
          const userData: User = {
            id: session.user.id,
            email: session.user.email || '',
            user_metadata: metadata,
            organizationId: metadata?.organization_id,
            firstName: firstName || undefined,
            lastName: lastName || undefined,
            role: metadata?.role,
          };

          setUser(userData);
          await initializeSync();
        } else if (event === 'SIGNED_OUT') {
          stopBackgroundSync();
          treeCache.clear();
          setUser(null);
          resetSyncState();
        }
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopBackgroundSync]); // initializeSync removed to prevent infinite loop (protected by syncInitializedRef)

  /**
   * Setup network event listeners
   */
  // Network listeners moved to hook

  const value: AuthContextType = useMemo(() => ({
    user,
    currentUser: user, // Alias for backward compatibility
    loading,
    isLoading: loading, // Alias
    error,
    login,
    logout,
    userId: user?.id || null,
    organizationId: user?.user_metadata?.organization_id || null,
    isAuthenticated: !!user,
    isSuperAdmin: user?.user_metadata?.role === 'super_admin',
    isCompanyAdmin: user?.user_metadata?.role === 'company_admin',
    isShipAdmin: user?.user_metadata?.role === 'ship_admin',
    isSyncing,
    lastSyncTime,
    syncError,
    bandwidthUsage,
    bandwidthSettings,
    updateBandwidthSettings,
    triggerManualSync,
  }), [
    user,
    loading,
    error,
    login,
    logout,
    isSyncing,
    lastSyncTime,
    syncError,
    bandwidthUsage,
    bandwidthSettings,
    updateBandwidthSettings,
    triggerManualSync,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Sprint 2025-11-012: Session expiry warning toast */}
      <SessionExpiryToast
        isVisible={showExpiryWarning}
        onExtend={handleExtendSession}
        onLogout={logout}
      />
    </AuthContext.Provider>
  );
}

/**
 * Hook to use auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

